import { serviceTypeEnum, tokenType } from '../store/API/types';
import { BookmarkType } from "../store/bookmarks/types";
import { EventType } from "../store/alerts/types";
import MyDate from "./MyDate";
import { RefObject } from "react";
import { BrowseFrameHTMLElement } from '../services/types';
import { ServiceAdapter } from '../services';
import { resetTimestrips } from "../store/timeline/actions";
import { Dispatch } from "redux";

const { CONFIG } = window;


export type StripType = {
    low: number,
    high: number,
    type: number
}

export type paneSizeType = {
    width: number,
    height: number
}

export type certAvailableType = {
    name: string,
    available: boolean
}

export enum StripTypeEnum {
    recording = 1,
    alarm = 2,
    noRecording = 5
}

export const chooseService = <T , U>(ocularis: () => T, cayuga: () => U) => {
    switch (window.CONFIG.ServerType) {
        case serviceTypeEnum.ocularis:
            return ocularis();
        case serviceTypeEnum.cayuga:
            return cayuga();
        default:
            return ocularis();
    }
}

export const getRotScaleFromCSSTranform = (el: HTMLElement) => {
    const tr: string | null = getComputedStyle(el, null).transform;

    if(tr === 'none') return {scale: 1, rotation: 0};

    if (tr) {
        let values1 = tr.split('(')[1];
        let values2 = values1.split(')')[0];
        let values = values2.split(',');

        const a: number = +values[0];
        const b: number = +values[1];

        const scale = Math.sqrt(a*a + b*b);
        const rotation = Math.round(Math.atan2(b, a) * (180/Math.PI));

        return { scale, rotation };
    } else {
        return {scale: 1, rotation: 0};
    }
};

export const adjustCameraElement = (elem: HTMLDivElement | null) => {
    if (!elem) return;

    const {top, left, height, width} = elem.getBoundingClientRect();
    const { innerWidth, innerHeight } = window;
    const footerHeight = innerHeight / 100 * 7;
    const shift = 10;                                                   // shift because of close icon

    let deltaY = innerHeight - (top + height / 2 + footerHeight);
    const deltaX = innerWidth - (left + width + shift);
    let countTop = deltaY > 0 ? -height / 2 : -height / 2 + deltaY;
    if (top < height / 2) countTop = -top + shift;

    const countWidth = deltaX > 0 ? 0 : deltaX;
    elem.style.setProperty('top', countTop + 'px');
    elem.style.setProperty('left', countWidth + 'px');
};

export const resetCameraElement = (elem: HTMLDivElement | null) => {
    if (!elem) return;

    elem.style.setProperty('top', 0 + 'px');
    elem.style.setProperty('left', 0 + 'px');
};

export const getLocalPosition = (elem: HTMLDivElement, globalPoint: {x: number, y: number}, scale: number) => {
    const { left, top, width, height } = elem.getBoundingClientRect();

    const delta = {
        x: (left + (width - width / scale) * 0.5),
        y: (top + (height - height / scale) * 0.5)
    };

    return {
        x: globalPoint.x - delta.x,
        y: globalPoint.y - delta.y,
    };
};

export const getLocalBounds = (el: HTMLElement | null) => {
    if (el) {
        const {width, height} = getComputedStyle(el);

        const w = width ? +width.slice(0, width.length - 2) : 0;
        const h = height ? +height.slice(0, height.length - 2) : 0;
        return { width: w, height: h }
    } else {
        return { width: 0, height: 0 }
    }
};
//
// export interface IGetCameraFromList<T> {
//     (cameras: Array<T>, id: string | null): T
// }

// export const getCameraFromList = <T>(cameras: T[], id: string | null): T => {
//     return chooseService(
//       () => cameras.filter(cam => cam && cam.id === id)[0],
//       () => cameras.filter(cam => cam && cam['a:EntityID'] === id)[0]
//     )
// };
export const getCameraFromList = (cameras: any[], id: string | null) => {
    const filteredCam = chooseService(
      () => cameras.filter(cam => cam && cam.id === id)[0],
      () => cameras.filter(cam => cam && cam['a:EntityID'] === id)[0]
    );
    return filteredCam ? filteredCam : {};
};

export const getCameraFromQuid = (cameras: any[], quid: string) => {
    return cameras.filter(cam => cam && quid.indexOf(cam.id) > -1)[0]
};

export const checkNoStreams = (cam: any) => {
    if (!cam) return {error: true, msg: `no camera with id ${cam}`};

    const { streams } = cam;
    if (streams.length === 0) return {error: true, msg: `${cam.name} has no H.264 stream.`};

    return {error: null, msg: null}
};

const isSupportedFormat  = (stream: any, cam: any) => {
    const codec = (stream && stream.codec) && stream.codec.slice(6 - stream.codec.length);

    if (!CONFIG.supportedFormats.includes(codec)) {
        return {error: true, msg: `${cam.name} has no H.264 stream.`}
    }

    return {error: null, msg: ''}
};

const getDefaultStreamFromCamera = (cam: any) => {
    return cam.streams.filter((stream: any) => stream.is_default)[0];
};

const getDefeniteStreamFromCamera = (cam: any, quality: string) => {
    return cam.streams.filter((stream: any) => stream.label === quality)[0];
};

const getSupportedStreamFromCamera = (cam: any) => {
    return cam.streams.filter((stream: any) => !isSupportedFormat(stream, cam).error);
};

export const getBrowseStreamFromCamera = (cameras: any[], id: string, quality?: string) => {
    const cam = getCameraFromList(cameras, id);

    const defaultStream = quality ? getDefeniteStreamFromCamera(cam, quality) : getDefaultStreamFromCamera(cam);
    return defaultStream ? {error: null, stream: defaultStream} : {error: `${cam.name} has no H.264 stream`, stream: null}
};

export const getStreamFromCamera = (cameras: any[], id: string, pane: RefObject<HTMLDivElement>, quality?: string) => {
    const cam = getCameraFromList(cameras, id);
    const {error, msg} = checkNoStreams(cam);
    if (error) return {error: true, stream: msg};
    const paneSize = {
        width: pane.current ? pane.current.clientWidth : 0,
        height: pane.current ? pane.current.clientHeight : 0
    }
    
    const supportedStreams = getSupportedStreamFromCamera(cam);

    const defaultStream = filterStreamsByResolution(supportedStreams, paneSize);

    const isSupported = isSupportedFormat(defaultStream, cam);

    if (isSupported.error) return {error: isSupported.error, stream: isSupported.msg};

    return {error: null, stream: defaultStream}
};

export const filterCamerasBySearchQuery = (cameras: any, val: string) => {
    return cameras.filter((cam: any) => {
        const name = cam.name.toLowerCase();
        return name.includes(val.toLowerCase())
    });
}

export const filterStreamsByResolution = (streams: any, paneSize: paneSizeType) => {
    const { width, height } = paneSize && paneSize;
    const paneArea = width * height;

    return streams.length > 0 ? streams.reduce((prev: any, curr: any) => {
        return (Math.abs(curr.width * curr.height - paneArea) < Math.abs(prev.width * prev.height - paneArea) ? curr : prev);
    }) : null;
}


export const concatByTime = (strips: StripType[]) => {
    strips.sort((first, second) => {
        if (first.low === second.low) {
            return first.type > second.type ? -1 : 1;
        }
        return first.low - second.low;
    });

    strips.sort((first, second) => first.type - second.type);

    return strips.reduce((hash: StripType[], strip) => {
        if (!hash.length) {
            hash.push(strip);
        } else {
            const i = hash.length - 1;
            const {high, type} = hash[i];
            if (high >= strip.low && type === strip.type) {
                hash[i].high = Math.max(strip.high, high)
            } else {
                hash.push(strip)
            }
        }
        return hash
    }, [])
};

export const createStrip = (low: number, high: number, type: number) => {
    return { high: high, low: low, type: type }
};

export const contains = (data: StripType[], time: number) => {
    return data.some(({high, low, type}: StripType) => {
        if (type === 5 || !time) return false;
        return time <= high && time >= low
    })
};

export const checkCayugaStripType = (stripType: number) => {
    let type;
    switch (stripType) {
        case 0:
            type = StripTypeEnum.recording;
            break;
        case 1:
            type = StripTypeEnum.alarm;
            break;
        default:
            type = StripTypeEnum.noRecording;
            break;
    }
    return type;
}

///////////////////CALCULATING/////////////////////////////

export const getLinearCoefs = ({x1, y1} : {x1: number, y1: number}, {x2, y2} : {x2: number, y2: number}) : {k: number, b: number} =>  {
    const k : number = (y2 - y1) / (x2 - x1);
    const b : number = (y1 - k * x1);

    return { k, b }
};

export const generateId = () => {
    return '' + Math.random().toString(36).substr(2, 9);
};

//////////////////////////////
export type elType = (Node & ParentNode) | null;

export const childOf = (c: elType,p: elType) => {while((c=c ? c.parentNode: null)&&c!==p);return !!c};

export const doCopyText = (value: string) => {
    const el = document.createElement('input');
    document.body.appendChild(el);
    el.value = value;
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el)
};


///////////////////////bkms sorting /////////////////////

export const sortBookmarks = (bookmarksData: BookmarkType[], direction: number, type: string) => {
    switch (type) {
        case 'created': { sortByTime(bookmarksData, direction, 'occurred'); break }
        case 'classification': { sortByText(bookmarksData, direction, type); break; }
        case 'case': { sortByText(bookmarksData, direction, type); break; }
        case 'time': { sortByTime(bookmarksData, direction, 'start'); break; }
        case 'duration': { sortByNumber(bookmarksData, direction, type); break; }
        case 'label': { sortByText(bookmarksData, direction, 'id'); break; }
        case 'status': { sortByText(bookmarksData, direction, type); break; }
        case 'thumbnail': { sortByNumber(bookmarksData, direction, type); break; }
        default: { }
    }
};

export const sortEvents = (events: EventType[], direction: number, type: string) => {
    switch (type) {
        case 'prio': { sortByNumber(events, direction, type); break; }
        case 'when': { sortByText(events, direction, type); break; }
        case 'what': { sortByText(events, direction, type); break; }
        case 'time': { sortByTime(events, direction, type); break; }
        case 'state': { sortByNumber(events, direction, type); break; }
        case 'where': { sortByText(events, direction, type); break; }
        default: { }
    }
    return events ? [...events] : []
};

const sortByTime = (dataArray: any[], direction: number, type: string) => {
    dataArray.sort((prev: BookmarkType, cur: BookmarkType) => {
        //@ts-ignore
        if (direction === -1) {
            //@ts-ignore
            return (+new Date(prev[type])) - (+new Date(cur[type]))
        } else {
            //@ts-ignore
            return (+new Date(cur[type])) - (+new Date(prev[type]))
        }
    });
};

const sortByNumber = (dataArray: any[], direction: number, type: string) => {
    if (type === 'thumbnail') {
        debugger;
        dataArray.sort((prev, cur) => {
            if (direction === -1) {
                //@ts-ignore
                return prev.files.length - cur.files.length
            } else {
                //@ts-ignore
                return cur.files.length - prev.files.length
            }
        });
    } else {
        dataArray.sort((prev, cur) => {
            if (direction === -1) {
                //@ts-ignore
                return +prev[type] - +cur[type]
            } else {
                //@ts-ignore
                return +cur[type] - +prev[type]
            }
        });
    }
};

const sortByText = (dataArray: any[], direction: number, type: string) => {
    dataArray.sort( (prev, cur) => {
        let prevText, nextText;
        //@ts-ignore
        if (type === 'status' || type === 'id') {
            //@ts-ignore
            prevText = prev.files[0] && prev.files[0][type] ? prev.files[0][type] : '';
            //@ts-ignore
            nextText = cur.files[0] && cur.files[0][type] ? cur.files[0][type] : '';
        } else {
            //@ts-ignore
            prevText = prev[type] ? prev[type] : '';
            //@ts-ignore
            nextText = cur[type] ? cur[type] : '';
        }

        prevText = prevText.toLowerCase();
        nextText = nextText.toLowerCase();

        if (direction === -1) {
            return prevText < nextText ? -1 : 1
        } else {
            return prevText >= nextText ? -1 : 1
        }
    });
};

// ********* time pane ***********

export const updatePaneTime = (pane: number, valueTime: number, isLocalTime: boolean) => {
    const el = document.getElementById('pane-time-' + pane);
    if (el) el.innerText = isLocalTime
        ? new MyDate(valueTime).toLocaleString()
        : new MyDate(valueTime).to24String();
};

export const fetchPaneTime = (pane: number) => {
    const el = document.getElementById('pane-time-' + pane);
  if (el) el.style.color = 'red'
};

export const successPaneTime = (pane: number) => {
  const el = document.getElementById('pane-time-' + pane);
  if (el) el.style.color = 'white'
};

export const errorPaneTime = (pane: number) => {
  return fetchPaneTime(pane)
};

export const onImageLoadError = (elem: BrowseFrameHTMLElement, cb: (time?: string | number) => any, noVideoSrc: string, pane: number) => {
    const time = elem?.browseFrame?.startTime ? elem.browseFrame.startTime : new Date().getTime()
    elem.blocked = false;
    elem.src = noVideoSrc;
    elem.classList.add('no-video');
    elem.onload = null;
    cb && cb(time);
    errorPaneTime(pane);
}

export const onImageLoadSuccess = (elem: BrowseFrameHTMLElement, cb: (time?: string | number) => any, pane: number) => {
    const time = elem?.browseFrame?.startTime ? elem.browseFrame.startTime : new Date().getTime()
    elem.blocked = false;
    elem.classList.remove('no-video');
    cb && cb(time);
    successPaneTime(pane)
}

// **************** auth ***************

export const certDownload = (certName: string) => {
    fetch(`http://${window.CONFIG.ServerAddress}:${window.CONFIG.OMSHTTPPort}/certs/${certName}`)
        .then(response => response.blob())
        .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = certName;
            document.body.appendChild(a);
            a.click();
            a.remove();
        });
}

export const checkCertsAvailability = async (certsName: string[]) => {
    const certAvailability = await Promise.all<certAvailableType>(certsName.map(async (certName: string) => {
        try {
            const res: any = await fetch(`http://${window.CONFIG.ServerAddress}:${window.CONFIG.OMSHTTPPort}/certs/${certName}`);
            if (res.status === 200) {
                return {
                    name: certName,
                    available: true
                }
            } else {
                return {
                    name: certName,
                    available: false
                }
            }
        } catch (e) {
            return {
                name: certName,
                available: false
            }
        }

    }));
    
    return certAvailability.filter((cert: certAvailableType) => !cert.available);
}

export const playBackUpdateTimeCb = (time: number, dispatch: Dispatch) => (newTime: number) => {
    if (+newTime === time) return;
    dispatch(resetTimestrips(newTime))
};

export const doBrowseGoTo = (token: tokenType, time: number, pane: number, dir: number, camId: string, service: ServiceAdapter, cb?: (newTime: number) => void) => {
    const paneId = `thumbnail-${pane}`;
    const { width } = getLocalBounds(document.getElementById(paneId));
    chooseService(
      () => service.browseGoto({token, time, pane, id: paneId, dir, camId: camId, width: Math.floor(width)}),
      () => service.browseMoveStep({token, pane, time, id: paneId, cb: () => {}, dir, camId, width: Math.floor(width)})
    );
}
export const doCayugaBrowseGoTo = (token: tokenType, time: number, pane: number, dir: number, camId: string, service: ServiceAdapter) => {
    const paneId = `thumbnail-${pane}`;
    const { width } = getLocalBounds(document.getElementById(paneId));
    service.browseGoto({token, pane, time, id: paneId, dir, camId, width: Math.floor(width)});
}

export const getStripLowTime = (time: number, closest: StripType, strips: Array<StripType>, stripIndex: number) => {
    if (time < closest.low) {
        return strips[stripIndex].low;
    } else if (strips[stripIndex + 1]) {
        return strips[stripIndex + 1].low;
    } else {
        return null;
    }
}

export const getStripHighTime = (time: number, closest: StripType, strips: Array<StripType>, stripIndex: number) => {
    if (time > closest.high) {
        return strips[stripIndex].high;
    } else if (strips[stripIndex - 1]) {
        return strips[stripIndex - 1].high;
    } else {
        return null;
    }
}

export const skipGapsStipTime = (timestrips: Array<StripType>, time: number, dir: number, pane: number) => {
    const activeTimeStrips = timestrips.filter((strip: any) => strip.type !== 5 && strip.type !== 0);
    if (activeTimeStrips.length === 0) return errorPaneTime(pane);
    const closest = activeTimeStrips.reduce((prev: any, curr: any) => dir === 1
      ? ((Math.abs(prev.high - time) <= Math.abs(curr.high - time)) ? prev : curr)
      : ((Math.abs(curr.low - time) <= Math.abs(prev.low - time)) ? curr : prev));

    const stripIndex = activeTimeStrips.findIndex(({high, low}: StripType) => closest.high === high && closest.low === low);

    if (stripIndex === -1) return errorPaneTime(pane);
    let gotoTime: number | null;
    if (dir === 1) {
        gotoTime = getStripLowTime(time, closest, activeTimeStrips, stripIndex);
    } else {
        gotoTime = getStripHighTime(time, closest, activeTimeStrips, stripIndex);
    }
    if (gotoTime == null) return errorPaneTime(pane);

    return gotoTime;
}

export const checkSecondToken = (url: string) => {
    const token2 = window.localStorage.getItem('token2');
    let fetchUrl = url;
    if (token2) {
        const [ urlPart, params ] = url.split('?');
        fetchUrl = `${urlPart}?token2=${token2}&${params}`;
    }
    return fetchUrl;
}